import { CheckCircle2, Clock, FileText, Package, Plane, Truck } from 'lucide-react'
import { useMemo } from 'react'

import { useParcelStatus } from '@/api/shipping/queries/useParcelStatus'
import { ParcelStatus } from '@/api/shipping/types/ParcelStatus'
import { AppDialog } from '@/components/AppDialog'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import { cn } from '@/lib/utils'

interface Props {
  toTrackingNumber: string
  isOpen: boolean
  onOpenChange: (open: boolean) => void
}

// Map status codes to Hebrew status text and icons
const getStatusInfo = (status: ParcelStatus, index: number, currentIndex: number) => {
  const isActive = index === currentIndex
  const isPast = index < currentIndex

  // Map status codes to Hebrew text and icons
  const statusMap: Record<string, { text: string; icon: typeof CheckCircle2 }> = {
    '00': { text: 'מידע דיגיטלי התקבל', icon: Clock },
    '15': { text: 'פרטי טיסת החבילה התקבלו', icon: Plane },
    '21': { text: 'הגיעה למרכז לוגיסטי בארץ', icon: Package },
    '32': { text: 'תהליך שחרור המכס החל', icon: FileText },
    '45': { text: 'החבילה נחתה בנמל התעופה בן גוריון', icon: Plane },
    '48': { text: 'החבילה שוחררה ממכס', icon: Package },
    '50': { text: 'בדרך לנקודת האיסוף', icon: Truck },
    '51': { text: 'נמסרה / נאספה', icon: CheckCircle2 },
    '52': { text: 'נמסרה / נאספה', icon: CheckCircle2 },
    // Add more common status codes
    '10': { text: 'חבילה בטיפול', icon: Package },
    '20': { text: 'יציאה מהמחסן', icon: Truck },
  }

  // Default mapping for unknown codes
  const defaultInfo = { text: status.status, icon: Clock }

  const statusInfo = statusMap[status.code] || defaultInfo
  const Icon = statusInfo.icon

  return {
    text: statusInfo.text,
    Icon,
    isActive,
    isPast,
  }
}

const formatDate = (dateString: string) => {
  const date = new Date(dateString)
  return date.toLocaleDateString('he-IL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

const formatShippingDate = (dateString: string) => {
  const date = new Date(dateString)
  return date.toLocaleDateString('he-IL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

export const DeliveryStatus = ({ toTrackingNumber, isOpen, onOpenChange }: Props) => {
  const { data: statusData, isLoading, error } = useParcelStatus(toTrackingNumber, isOpen)

  // Statuses come in reverse chronological order (newest first), so reverse them
  const sortedStatuses = useMemo(() => {
    if (!statusData?.statuses) return []
    // Reverse to show chronological order (oldest first)
    return [...statusData.statuses].reverse()
  }, [statusData])

  // Find the current/active status (usually the latest non-delivered status)
  const currentStatusIndex = useMemo(() => {
    if (!sortedStatuses || sortedStatuses.length === 0) return -1

    // Find the delivered status (code 51 or 52)
    const deliveredIndex = sortedStatuses.findIndex(s => s.code === '51' || s.code === '52')

    // If delivered, that's the current one (highlighted)
    if (deliveredIndex !== -1) return deliveredIndex

    // If no delivered status, the last one (most recent) is current
    return sortedStatuses.length - 1
  }, [sortedStatuses])

  // Get shipping date from the earliest status or package
  const shippingDate = useMemo(() => {
    if (sortedStatuses && sortedStatuses.length > 0) {
      // The first status in sorted array is the earliest
      const earliestStatus = sortedStatuses[0]
      return formatShippingDate(earliestStatus.dateTime)
    }
  }, [sortedStatuses])

  return (
    <AppDialog isOpen={isOpen} onOpenChange={onOpenChange}>
      <div className='flex flex-col gap-4 pt-4'>
        {isLoading && (
          <div className='flex flex-col items-center justify-center gap-4 py-8'>
            <p className='text-text-secondary'>טוען סטטוס משלוח...</p>
            <Spinner />
          </div>
        )}

        {!!error && (
          <div className='flex flex-col items-center justify-center gap-4 py-8'>
            <p className='text-text-secondary text-center'>שגיאה בטעינת סטטוס המשלוח</p>
            <Button variant='outline' onClick={() => onOpenChange(false)}>
              סגור
            </Button>
          </div>
        )}

        {!isLoading && !error && statusData && (
          <>
            {/* Shipping Info */}
            <div className='flex flex-col gap-1'>
              <p className='text-text-primary text-base font-medium'>סטטוס המשלוח שלך</p>
              <p className='text-text-secondary text-xs'>
                ב-{shippingDate} נשלח עם Excelot | מספר מעקב: {statusData.parcel.trackingNumber}
              </p>
            </div>

            {/* Status Timeline */}
            <div className='flex max-h-[400px] flex-col gap-[27px] overflow-y-auto px-2'>
              {sortedStatuses.map((status, index) => {
                const { text, Icon, isActive, isPast } = getStatusInfo(status, index, currentStatusIndex)
                const statusDate = formatDate(status.dateTime)

                return (
                  <div
                    key={`${status.code}-${status.dateTime}`}
                    className={cn('flex items-start justify-end gap-1', isActive && 'bg-button-primary-selected-segment rounded-xl p-2')}
                  >
                    <div className='flex grow flex-col gap-1'>
                      <p className={cn('text-sm', isActive ? 'text-text-primary' : 'text-text-disabled', isPast && 'text-text-secondary')}>
                        {text}
                      </p>
                      <p className={cn('text-xs', isActive ? 'text-text-secondary' : 'text-text-disabled', isPast && 'text-text-disabled')}>
                        {statusDate}
                      </p>
                    </div>
                    <Icon
                      className={cn('size-5', isActive ? 'text-text-primary' : 'text-text-disabled', isPast && 'text-text-secondary')}
                    />
                  </div>
                )
              })}
            </div>

            {/* Delivery Link */}
            <a
              href={`https://myparcel.exelot.com/tracking?barcodes=${statusData.parcel.trackingNumber}`}
              target='_blank'
              rel='noopener noreferrer'
            >
              <Button variant='link' className='text-text-link hover:text-text-link/80 text-sm underline underline-offset-2'>
                לפרטים המלאים של סטטוס המשלוח
              </Button>
            </a>
          </>
        )}
      </div>
    </AppDialog>
  )
}
